'use client';

import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { toast } from '@kit/ui/sonner';
import { cn } from '@kit/ui/utils';

interface CopyLinkButtonProps {
  /** URL (or any text) written to the clipboard on click. */
  value: string;
  /** Label before copy. Defaults to the translated "Copy link". */
  label?: string;
  /** Label shown for 2s after a successful copy. Defaults to the translated "Copied". */
  copiedLabel?: string;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

/**
 * Copies `value` to the clipboard, flips to a checkmark + toast on success,
 * and resets after 2s. Extracted from the copy-to-clipboard pattern in
 * `community/feed/share-dialog.tsx` so the marketplace share UI and any
 * future caller consume one component instead of re-implementing it.
 */
export function CopyLinkButton({
  value,
  label,
  copiedLabel,
  variant = 'outline',
  size = 'default',
  className,
}: CopyLinkButtonProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const resolvedLabel = label ?? t('common:copyLink.label');
  const resolvedCopiedLabel = copiedLabel ?? t('common:copyLink.copiedLabel');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(t('common:copyLink.toast.success'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('common:copyLink.toast.error'));
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={handleCopy}
      className={cn(className)}
    >
      {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
      {size === 'icon' ? null : copied ? resolvedCopiedLabel : resolvedLabel}
    </Button>
  );
}
